// Console D's small instruments (the mock's core.js): a share bar, a sparkline, bars, two
// stacked steps and a sankey. Ink on hairlines, one accent at most, every chart named for
// screen readers. LineChart (../LineChart.tsx) is the line instrument. Charts with axes draw at
// their container's real width (viewBox = pixels), so their text keeps its size on a phone.
import { type ReactNode, useMemo } from 'react'
import {
  ChartAria,
  ChartFocus,
  ChartLegend,
  ChartOverlay,
  ChartTable,
  ChartTools,
  ChartTooltip,
  readout,
  useChart,
  useChartIds,
} from '../chart/useChart'
import { useWidth } from '../LineChart'
import { useChartText } from '../text'

const fmt2 = (v: number) =>
  Math.abs(v) >= 100 ? v.toFixed(0) : Math.abs(v) >= 10 ? v.toFixed(1) : v.toFixed(2)

/** A 64 px share bar beside a number; `bad` and `warn` tint the fill. */
export function Hbar({ share, tone }: { share: number; tone?: 'bad' | 'warn' }) {
  const pct = Math.max(0, Math.min(100, share * 100))
  return (
    <span className={tone ? `hbar ${tone}` : 'hbar'} aria-hidden="true">
      <i style={{ width: `${pct}%` }} />
    </span>
  )
}

/**
 * A 64×18 sparkline. Decoration: the numbers beside it carry the meaning. A null is a gap (a day
 * with nothing to measure), never a zero: the line breaks there.
 */
export function Spark({ values }: { values: readonly (number | null)[] }) {
  // The tallest value fills the height, whatever its scale (a count, a rate).
  const max = Math.max(...values.map((v) => v ?? 0)) || 1
  const n = values.length
  const x = (i: number) => (n > 1 ? (i / (n - 1)) * 62 + 1 : 32)
  const y = (v: number) => 16 - (v / max) * 14
  // Runs of measured points between the gaps.
  const runs: [number, number][][] = [[]]
  values.forEach((v, i) => {
    if (v === null) runs.push([])
    else runs.at(-1)?.push([x(i), y(v)])
  })
  return (
    <svg className="spark" viewBox="0 0 64 18" aria-hidden="true" focusable="false">
      {runs
        .filter((r) => r.length > 0)
        .map((r) => {
          const [first] = r as [[number, number]]
          // A lone point between gaps still shows, as a one-unit tick.
          const pts = r.length > 1 ? r : [first, [first[0] + 1, first[1]]]
          return <polyline key={first[0]} points={pts.map((p) => p.join(',')).join(' ')} />
        })}
    </svg>
  )
}

/**
 * The chart's focus box only when its bars are named: without names there is nothing to read out,
 * so no tab stop promises arrows and a table (Loom). The plain <svg> (role img) stays.
 */
function MaybeFocus({
  on,
  children,
  ...focus
}: { on: boolean; children: ReactNode } & Omit<Parameters<typeof ChartFocus>[0], 'children'>) {
  return on ? <ChartFocus {...focus}>{children}</ChartFocus> : children
}

/**
 * Whether a line drawn before bar `index` (a release marker, a percentile) shows while bars
 * `from` to `to` do: from the left edge of the first to the right edge of the last (APS-577, one
 * rule for Bars and StackBars).
 */
export const inView = (index: number, from: number, to: number): boolean =>
  index >= from && index <= to + 1

/** One series of bars: its name (for the legend) and a value per category; null draws nothing. */
/** A percentile line's label, placed: beside its line, `end` when it reads to the left. */
export interface MarkLabel {
  x: number
  y: number
  end: boolean
}

/**
 * Labels for vertical lines that never overprint (APS-382): each sits right of its line, or left
 * of it when the right side runs past the plot or into the next line; one that fits neither way
 * drops a row. A label crosses another line only when no row keeps it clear.
 */
export function placeMarkLabels(
  marks: readonly { x: number; text: string }[],
  left: number,
  right: number,
  top: number,
  row = 12,
): MarkLabel[] {
  const CHAR = 6.6
  const order = marks.map((_, i) => i).sort((a, b) => (marks[a]?.x ?? 0) - (marks[b]?.x ?? 0))
  const placed: { from: number; to: number; y: number }[] = []
  const out: MarkLabel[] = marks.map(() => ({ x: 0, y: top, end: false }))
  for (const i of order) {
    const m = marks[i] as { x: number; text: string }
    const w = m.text.length * CHAR
    const sides = [
      { from: m.x + 5, to: m.x + 5 + w, end: false },
      { from: m.x - 5 - w, to: m.x - 5, end: true },
    ].filter((s) => s.from >= left && s.to <= right)
    const options = sides.length > 0 ? sides : [{ from: m.x - 5 - w, to: m.x - 5, end: true }]
    const crosses = (s: { from: number; to: number }) =>
      marks.some((o, j) => j !== i && o.x >= s.from - 2 && o.x <= s.to + 2)
    const free = (s: { from: number; to: number }, y: number) =>
      !placed.some((p) => p.y === y && s.from < p.to + 4 && p.from < s.to + 4)
    let pick: { from: number; to: number; end: boolean; y: number } | undefined
    for (const clear of [true, false])
      for (let r = 0; r < marks.length && !pick; r++)
        for (const s of options)
          if (!pick && free(s, top + r * row) && (!clear || !crosses(s)))
            pick = { ...s, y: top + r * row }
    const at = pick ?? { ...(options[0] as (typeof options)[number]), y: top }
    placed.push(at)
    out[i] = { x: at.end ? m.x - 5 : m.x + 5, y: at.y, end: at.end }
  }
  return out
}

export interface BarSeries {
  name: string
  values: readonly (number | null)[]
}

export interface BarsProps {
  /** One series, as the mock draws bars. Or `series`. */
  values?: readonly number[]
  /** Several series side by side in each category, in steps of ink, with a legend. */
  series?: readonly BarSeries[]
  label: string
  width?: number
  height?: number
  max?: number
  format?: (v: number) => string
  /** x labels: [index, text]. */
  xLabels?: readonly (readonly [number, string])[]
  /**
   * A label per category, centred under its bar (instead of `xLabels`): every one while the slots
   * are wide enough, shortened with an ellipsis (the full text in a title); thinned when not.
   */
  categories?: readonly string[]
  /** The share of each slot left empty between bars (0 to 0.6); 2 px when unset. */
  gapShare?: number
  /** Bars from this index on are full ink (the rest are a step lighter). */
  hiFrom?: number
  /** A class per bar (b1, b1 hi, b2, b3, bw, bc). */
  classes?: readonly (string | undefined)[]
  /** A dashed release marker before a bar. */
  marker?: { index: number; label: string }
  /** Percentile lines before a bar (p50, p90), dashed in the degraded colour. */
  percentiles?: readonly { index: number; label: string }[]
  /** A budget or an alert's limit: a dashed horizontal line with its label. */
  threshold?: { value: number; label: string } | undefined
  /**
   * Each bar's full name, for its tooltip and the table ("Oct 3", "250 – 500 ms"), where the axis
   * labels only some (APS-577). `categories` serve when unset.
   */
  labels?: readonly string[]
  /** The x axis is time: a drag zooms into a span of bars, Reset zoom undoes it (APS-577). */
  timeAxis?: boolean
  /** A click (or Enter) on a bar opens a filtered list: its index and series. */
  onPick?: ((index: number, series: number) => void) | undefined
  /** What a click on that bar opens, as the tooltip's last line. */
  pickHint?: ((index: number, series: number) => string | undefined) | undefined
  /** The Table toggle; off where the view already lists these values. */
  table?: boolean
  /** The table's first column (the x axis's name). */
  xName?: string
  /**
   * A value as the tooltip and the table say it, with its noun ("3 calls"), where the axis's
   * format is a bare number (Mosaic). The axis format when unset.
   */
  tipFormat?: (v: number) => string
}

/**
 * Bars on an axis from zero (below it for negative values) with three gridlines. One series is
 * the mock's bars; several sit side by side in each category, as the line chart's steps of ink.
 * Each slot is its own hit target (the whole slot, gaps included): its tooltip lists every
 * series in it, and the hovered bars lift (APS-577).
 */
export function Bars({
  values,
  series,
  label,
  width: fixedWidth,
  height = 160,
  max: maxIn,
  format = fmt2,
  xLabels = [],
  categories,
  gapShare,
  hiFrom,
  classes,
  marker,
  percentiles = [],
  threshold,
  labels,
  timeAxis = false,
  onPick,
  pickHint,
  table = true,
  xName,
  tipFormat,
}: BarsProps) {
  const [ref, measured] = useWidth(640)
  const ids = useChartIds()
  const text = useChartText()
  const width = fixedWidth ?? measured
  const L = 40
  const R = 12
  const T = 12
  const B = 22
  const allSets: readonly (readonly (number | null)[])[] = series
    ? series.map((x) => x.values)
    : [values ?? []]
  const several = allSets.length > 1
  const total = Math.max(0, ...allSets.map((x) => x.length))
  const allKeys = useMemo(() => Array.from({ length: total }, (_, i) => i), [total])
  const zoomable = timeAxis && total > 2
  const seriesNames = series ? series.map((x) => x.name) : [text.value]
  const chart = useChart({
    keys: allKeys,
    names: seriesNames,
    zoomable,
    data: (labels ?? categories ?? []).join('\u001f'),
  })
  const { from, to, zoomed, isHidden } = chart
  const cut = <T,>(list: readonly T[] | undefined) => list?.slice(from, to + 1)
  // What the chart shows: the zoomed span; a hidden series keeps its slot place (and its class),
  // so the others neither move nor repaint.
  const sets = allSets.map((set) => set.slice(from, to + 1))
  const shownCats = cut(categories)
  const names = cut(labels) ?? shownCats
  const n = Math.max(0, ...sets.map((x) => x.length))
  const known = [
    ...sets.flatMap((x, s) => (isHidden(s) ? [] : x.filter((v): v is number => v !== null))),
    ...(threshold ? [threshold.value] : []),
  ]
  const min = Math.min(0, ...known)
  const max = maxIn ?? Math.max(0, ...known)
  const top = max > min ? max : min + 1
  const bw = (width - L - R) / Math.max(1, n)
  const y = (v: number) => T + (1 - (v - min) / (top - min)) * (height - T - B)
  const a = chart.active
  const out: ReactNode[] = []
  for (let k = 0; k <= 2; k++) {
    const v = min + (k / 2) * (top - min)
    out.push(
      <g key={`g${k}`}>
        <line className="grid" x1={L} x2={width - R} y1={y(v)} y2={y(v)} />
        <text className="ax" x={L - 6} y={y(v) + 3.5} textAnchor="end">
          {format(v)}
        </text>
      </g>,
    )
  }
  const gap = gapShare === undefined ? 2 : bw * Math.min(0.6, Math.max(0, gapShare))
  const inner = Math.max(1, (bw - gap) / sets.length)
  const shownHi = hiFrom === undefined ? undefined : hiFrom - from
  for (const [s, set] of sets.entries()) {
    if (isHidden(s)) continue
    for (const [i, v] of set.entries()) {
      if (v === null) continue
      const base = several
        ? `vbar s${s}`
        : (classes?.[from + i] ?? (shownHi !== undefined && i >= shownHi ? 'b1 hi' : 'b1'))
      out.push(
        <rect
          key={`b${s}-${i}`}
          className={i === a ? `${base} on` : base}
          x={L + i * bw + gap / 2 + s * inner}
          y={Math.min(y(v), y(0))}
          width={Math.max(1, inner - (several ? 1 : 0))}
          height={Math.abs(y(0) - y(v))}
        />,
      )
    }
  }
  out.push(<line key="base" className="base" x1={L} x2={width - R} y1={y(0)} y2={y(0)} />)
  // Every category while its slot holds at least a few characters, shortened to the slot;
  // otherwise thinned to what fits (at most 3 on a phone and 6 wider, fewer when they are long).
  const CHAR = 6.6
  const slotChars = Math.floor((bw - 6) / CHAR)
  const longest = Math.max(0, ...(shownCats ?? []).map((c) => c.length))
  const fit = Math.max(1, Math.floor((width - L - R) / (longest * CHAR + 12)))
  const every =
    shownCats && slotChars >= 4 ? 1 : Math.max(1, Math.ceil(n / Math.min(width < 480 ? 3 : 6, fit)))
  const short = (t: string) =>
    every === 1 && t.length > slotChars ? `${t.slice(0, Math.max(1, slotChars - 1))}…` : t
  // Zoomed, the caller's edge labels name the whole span's ends: the zoomed span's ends instead.
  const edges: readonly (readonly [number, string])[] =
    zoomed && names && n > 0
      ? [
          [0, names[0] ?? ''],
          [n - 1, names[n - 1] ?? ''],
        ]
      : xLabels
          .filter(([i]) => i >= from - 0.5 && i <= to + 0.5)
          .map(([i, t]) => [i - from, t] as const)
  const xs: readonly (readonly [number, string])[] = shownCats
    ? shownCats.flatMap((t, i) => (i % every === 0 ? [[i, t] as const] : []))
    : edges
  for (const [i, t] of xs) {
    // Category labels sit centred under their bar; the mock's x labels keep their edge anchors.
    const centred = !!shownCats
    const shown = centred ? short(t) : t
    out.push(
      <text
        key={`x${i}`}
        className="ax"
        x={L + i * bw + bw / 2}
        y={height - 6}
        textAnchor={centred ? 'middle' : i === 0 ? 'start' : i >= n - 1 ? 'end' : 'middle'}
      >
        {shown !== t ? <title>{t}</title> : null}
        {shown}
      </text>,
    )
  }
  const shownPct = percentiles
    .filter((p) => inView(p.index, from, to))
    .map((p) => ({ ...p, index: p.index - from }))
  const marks = shownPct.map((p) => ({ x: L + p.index * bw, text: p.label }))
  const at = placeMarkLabels(marks, L, width - R, T + 9)
  for (const [i, p] of shownPct.entries()) {
    const px = marks[i]?.x ?? 0
    const l = at[i]
    out.push(
      <g key={`p${p.index}${p.label}`}>
        <line className="thr" x1={px} x2={px} y1={T} y2={height - B} />
        <text
          className="thr-t"
          x={l?.x ?? px + 5}
          y={l?.y ?? T + 9}
          {...(l?.end ? { textAnchor: 'end' } : {})}
        >
          {p.label}
        </text>
      </g>,
    )
  }
  if (threshold)
    out.push(
      <g key="thr">
        <line
          className="thr"
          x1={L}
          x2={width - R}
          y1={y(threshold.value)}
          y2={y(threshold.value)}
        />
        <text className="thr-t" x={width - R} y={y(threshold.value) - 5} textAnchor="end">
          {threshold.label}
        </text>
      </g>,
    )
  if (marker && inView(marker.index, from, to)) {
    const px = L + (marker.index - from) * bw
    out.push(
      <g key="mk">
        <line className="mk" x1={px} x2={px} y1={T - 4} y2={height - B} />
        <text className="mk-t" x={px + 5} y={T + 6}>
          {marker.label}
        </text>
      </g>,
    )
  }

  // The interaction layer (APS-577): a slot's every shown series. A tooltip and a table need a
  // name for every bar (labels or categories); without, the bars stay as they are (Loom).
  const named = !!names && names.length >= n && n > 0
  const centres = Array.from({ length: n }, (_, i) => L + i * bw + bw / 2)
  const rowsAt = (i: number) =>
    sets.flatMap((set, s) =>
      isHidden(s)
        ? []
        : [
            {
              series: s,
              name: several ? (seriesNames[s] ?? '') : '',
              value:
                set[i] === null || set[i] === undefined
                  ? text.none
                  : (tipFormat ?? format)(set[i] as number),
              box: true,
            },
          ],
    )
  const firstShown = sets.findIndex((_, s) => !isHidden(s))
  const pick = onPick
    ? (i: number, pt: { x: number; y: number } | null) => {
        // Side by side, the bar under the pointer; from the keyboard, the first shown.
        let s = Math.max(0, firstShown)
        if (pt && several) {
          const k = Math.floor((pt.x - (L + i * bw + gap / 2)) / inner)
          if (k >= 0 && k < sets.length && !isHidden(k)) s = k
        }
        onPick(from + i, s)
      }
    : undefined
  const rows = a >= 0 && named ? rowsAt(a) : []
  const hint = a >= 0 && named && onPick ? pickHint?.(from + a, Math.max(0, firstShown)) : undefined
  const title = a >= 0 ? (names?.[a] ?? '') : ''
  const rowLabel = (i: number) => names?.[i] ?? ''
  return (
    <div ref={ref} className="ch-wrap">
      <MaybeFocus
        on={named}
        chart={chart}
        label={label}
        ids={ids}
        allKeys={allKeys}
        zoomable={zoomable}
        onPick={pick}
        width={width}
      >
        <svg
          className="chart"
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          height={height}
          role="img"
          aria-label={label}
        >
          {out}
          {named ? (
            <ChartOverlay
              chart={chart}
              xs={centres}
              allKeys={allKeys}
              width={width}
              left={L}
              right={width - R}
              top={T}
              bottom={height - B}
              crosshair={false}
              zoomable={zoomable}
              onPick={pick}
            />
          ) : null}
        </svg>
      </MaybeFocus>
      {a >= 0 && named && centres[a] !== undefined ? (
        <ChartTooltip
          x={centres[a] as number}
          width={width}
          top={T}
          title={title}
          rows={rows}
          hint={hint}
          beside={bw / 2}
        />
      ) : null}
      <ChartAria
        ids={ids}
        chart={chart}
        text={a >= 0 && named ? readout(title, rows, hint) : ''}
        canOpen={!!onPick}
        canZoom={zoomable}
      />
      {several && series ? (
        <ChartLegend names={seriesNames} box hidden={chart.hidden} onToggle={chart.toggle} />
      ) : null}
      <ChartTools
        chart={chart}
        table={table && named}
        zoomedFrom={rowLabel(0)}
        zoomedTo={rowLabel(n - 1)}
        tableId={ids.table}
      />
      {table && named && chart.table ? (
        <ChartTable
          id={ids.table}
          label={label}
          xName={xName}
          series={seriesNames.filter((_, s) => !isHidden(s))}
          rows={Array.from({ length: n }, (_, i) => ({
            label: rowLabel(i),
            values: rowsAt(i).map((r) => r.value),
          }))}
        />
      ) : null}
    </div>
  )
}

/**
 * Two stacked ink steps a bar: `a` (full ink) over `b` (a step lighter). Each bar's slot is its
 * hit target; its tooltip gives both steps (APS-577). With `names`, a legend names the steps and
 * hides or isolates one.
 */
export function StackBars({
  a,
  b,
  label,
  width: fixedWidth,
  height = 120,
  max: maxIn,
  xLabels = [],
  marker,
  names,
  labels,
  format = (v: number) => String(v),
  timeAxis = false,
  onPick,
  pickHint,
  table = true,
  xName,
  tipFormat,
}: {
  a: readonly number[]
  b: readonly number[]
  label: string
  width?: number
  height?: number
  max?: number
  xLabels?: readonly (readonly [number, string])[]
  marker?: { index: number; label: string }
  /**
   * The two steps' names, `a` then `b`: a legend (box swatches) whose items hide or isolate. A
   * step named '' is not one (no legend; left out of the tooltip and the table).
   */
  names?: readonly [string, string]
  /** Each bar's full name, for its tooltip and the table ("Oct 3"). */
  labels?: readonly string[]
  /** A value in the tooltip and the table. */
  format?: (v: number) => string
  /** The x axis is time: a drag zooms into a span of bars, Reset zoom undoes it. */
  timeAxis?: boolean
  /** A click (or Enter) on a bar opens a filtered list: its index and step (0 `a`, 1 `b`). */
  onPick?: ((index: number, series: number) => void) | undefined
  pickHint?: ((index: number, series: number) => string | undefined) | undefined
  table?: boolean
  xName?: string
  /**
   * A value as the tooltip and the table say it, with its noun ("3 calls"), where the axis's
   * format is a bare number (Mosaic). The axis format when unset.
   */
  tipFormat?: (v: number) => string
}) {
  const [ref, measured] = useWidth(640)
  const ids = useChartIds()
  const width = fixedWidth ?? measured
  const L = 32
  const R = 8
  const T = 12
  const B = 22
  const total = a.length
  const allKeys = useMemo(() => Array.from({ length: total }, (_, i) => i), [total])
  const zoomable = timeAxis && total > 2
  const stepNames = names ?? ['', '']
  const chart = useChart({
    keys: allKeys,
    names: stepNames,
    zoomable,
    data: (labels ?? []).join('\u001f'),
  })
  const { from, to, zoomed, isHidden } = chart
  const showA = !isHidden(0)
  const showB = !isHidden(1)
  const av = a.slice(from, to + 1)
  const bv = b.slice(from, to + 1)
  const shownLabels = labels?.slice(from, to + 1)
  const n = av.length
  const bw = (width - L - R) / Math.max(1, n)
  const max =
    maxIn ?? Math.max(...av.map((v, i) => (showA ? v : 0) + (showB ? (bv[i] ?? 0) : 0)), 0)
  // Counts: whole-number ticks only, so the top is even (its middle a whole number) or 1.
  const top = max > 1 ? Math.ceil(max / 2) * 2 : 1
  const y = (v: number) => T + (1 - v / top) * (height - T - B)
  const act = chart.active
  const out: ReactNode[] = []
  for (const v of top > 1 ? [0, top / 2, top] : [0, 1]) {
    out.push(
      <g key={`g${v}`}>
        <line className="grid" x1={L} x2={width - R} y1={y(v)} y2={y(v)} />
        <text className="ax" x={L - 6} y={y(v) + 3.5} textAnchor="end">
          {v}
        </text>
      </g>,
    )
  }
  av.forEach((va, i) => {
    const x0 = L + i * bw + 3
    const w = Math.max(1, bw - 6)
    const hb = showB ? height - B - y(bv[i] ?? 0) : 0
    const ha = showA ? height - B - y(va) : 0
    const on = i === act ? ' on' : ''
    out.push(
      // biome-ignore lint/suspicious/noArrayIndexKey: bars are positional
      <g key={`s${i}`}>
        {showB ? (
          <rect className={`b2${on}`} x={x0} y={height - B - hb} width={w} height={hb} />
        ) : null}
        {showA ? (
          <rect className={`b1 hi${on}`} x={x0} y={height - B - hb - ha} width={w} height={ha} />
        ) : null}
      </g>,
    )
  })
  out.push(
    <line key="base" className="base" x1={L} x2={width - R} y1={height - B} y2={height - B} />,
  )
  // Zoomed, the caller's edge labels name the whole span's ends: the zoomed span's ends instead.
  const edges: readonly (readonly [number, string])[] =
    zoomed && shownLabels && n > 0
      ? [
          [0, shownLabels[0] ?? ''],
          [n - 1, shownLabels[n - 1] ?? ''],
        ]
      : xLabels.filter(([i]) => i >= from && i <= to).map(([i, t]) => [i - from, t] as const)
  for (const [i, t] of edges)
    out.push(
      <text
        key={`x${i}`}
        className="ax"
        x={L + i * bw + (i ? bw : 0)}
        y={height - 6}
        textAnchor={i ? 'end' : 'start'}
      >
        {t}
      </text>,
    )
  if (marker && inView(marker.index, from, to)) {
    const mi = marker.index - from
    const px = L + mi * bw
    const span = marker.label.length * 6.6 + 5
    const tall = (i: number) => y((showA ? (av[i] ?? 0) : 0) + (showB ? (bv[i] ?? 0) : 0)) < 20
    const rightBlocked = (at: number) => {
      if (px + span > width - R) return true
      for (let i = at; i < n && L + i * bw < px + span; i++) if (tall(i)) return true
      return false
    }
    out.push(
      <g key="mk">
        <line className="mk" x1={px} x2={px} y1={4} y2={height - B} />
        <text
          className="mk-t"
          // Right of the line, unless the bars there reach the label or the edge cuts it.
          {...(rightBlocked(mi) ? { x: px - 5, textAnchor: 'end' } : { x: px + 5 })}
          y={12}
        >
          {marker.label}
        </text>
      </g>,
    )
  }

  // The interaction layer (APS-577): both steps of a bar. A tooltip and a table need each bar's
  // name and the steps' names; without, the bars stay as they are (Loom).
  const named = !!names && !!shownLabels && shownLabels.length >= n && n > 0
  const centres = Array.from({ length: n }, (_, i) => L + i * bw + bw / 2)
  // A step named '' is no step (an issue with no latest release has one: all occurrences).
  const stepShown = (s: number) => !isHidden(s) && stepNames[s] !== ''
  const rowsAt = (i: number) =>
    [av[i], bv[i]].flatMap((v, s) =>
      !stepShown(s)
        ? []
        : [
            {
              series: s,
              name: stepNames[s] ?? '',
              value: (tipFormat ?? format)(v ?? 0),
              box: true,
            },
          ],
    )
  const pick = onPick
    ? (i: number, pt: { x: number; y: number } | null) => {
        // The step under the pointer; from the keyboard (or a hidden step), the first shown.
        let s = showA ? 0 : 1
        if (pt && showA && showB) {
          // Above the top of the lower step is the upper one.
          s = pt.y < y(bv[i] ?? 0) ? 0 : 1
        }
        onPick(from + i, s)
      }
    : undefined
  const rows = act >= 0 && named ? rowsAt(act) : []
  const hint = act >= 0 && named && onPick ? pickHint?.(from + act, showA ? 0 : 1) : undefined
  const title = act >= 0 ? (shownLabels?.[act] ?? '') : ''
  const hasTable = table && named
  return (
    <div ref={ref} className="ch-wrap">
      <MaybeFocus
        on={named}
        chart={chart}
        label={label}
        ids={ids}
        allKeys={allKeys}
        zoomable={zoomable}
        onPick={pick}
        width={width}
      >
        <svg
          className="chart"
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          height={height}
          role="img"
          aria-label={label}
        >
          {out}
          {named ? (
            <ChartOverlay
              chart={chart}
              xs={centres}
              allKeys={allKeys}
              width={width}
              left={L}
              right={width - R}
              top={T}
              bottom={height - B}
              crosshair={false}
              zoomable={zoomable}
              onPick={pick}
            />
          ) : null}
        </svg>
      </MaybeFocus>
      {act >= 0 && named && centres[act] !== undefined ? (
        <ChartTooltip
          x={centres[act] as number}
          width={width}
          top={T}
          title={title}
          rows={rows}
          hint={hint}
          beside={bw / 2}
        />
      ) : null}
      <ChartAria
        ids={ids}
        chart={chart}
        text={act >= 0 && named ? readout(title, rows, hint) : ''}
        canOpen={!!onPick}
        canZoom={zoomable}
      />
      {names?.every((x) => x !== '') ? (
        <ChartLegend names={names} box hidden={chart.hidden} onToggle={chart.toggle} />
      ) : null}
      <ChartTools
        chart={chart}
        table={hasTable}
        zoomedFrom={shownLabels?.[0] ?? ''}
        zoomedTo={shownLabels?.[n - 1] ?? ''}
        tableId={ids.table}
      />
      {hasTable && chart.table ? (
        <ChartTable
          id={ids.table}
          label={label}
          xName={xName}
          series={stepNames.filter((_, s) => stepShown(s))}
          rows={Array.from({ length: n }, (_, i) => ({
            label: shownLabels?.[i] ?? '',
            values: rowsAt(i).map((r) => r.value),
          }))}
        />
      ) : null}
    </div>
  )
}

export interface SankeyNode {
  id: string
  label: string
  value: number
  /** bad (crash colour), acc (the view's one accent), dim. */
  tone?: 'bad' | 'acc' | 'dim'
}
export interface SankeyLink {
  source: string
  target: string
  value: number
  tone?: 'bad' | 'acc'
}

/**
 * Paths as bands between columns of nodes (journeys, the paths before an exit). Drawn at its
 * container's width, like the mock's, so labels stay 11 px and the chart 260 px tall.
 */
export function Sankey({
  columns,
  links,
  label,
  width: fixedWidth,
  height = 260,
  right = 150,
  gap = 12,
  format = (v: number) => String(v),
}: {
  columns: readonly (readonly SankeyNode[])[]
  links: readonly SankeyLink[]
  label: string
  width?: number
  height?: number
  /** Room for the last column's labels. */
  right?: number
  gap?: number
  format?: (v: number) => string
}) {
  const [ref, measured] = useWidth(640)
  const width = fixedWidth ?? measured
  const L = 2
  const T = 6
  const B = 6
  const nw = 8
  const nc = columns.length
  const totals = columns.map((c) => c.reduce((a, n) => a + n.value, 0))
  const maxN = Math.max(...columns.map((c) => c.length), 1)
  const k = (height - T - B - (maxN - 1) * gap) / Math.max(...totals, 1)
  const pos = new Map<
    string,
    { x: number; y: number; h: number; n: SankeyNode; out: number; in: number }
  >()
  columns.forEach((c, i) => {
    const x = L + (nc === 1 ? 0 : (i * (width - L - right - nw)) / (nc - 1))
    let y = T
    for (const n of c) {
      const h = Math.max(2, n.value * k)
      pos.set(n.id, { x, y, h, n, out: y, in: y })
      y += h + gap
    }
  })
  const bands: ReactNode[] = []
  links.forEach((l, i) => {
    const a = pos.get(l.source)
    const b = pos.get(l.target)
    if (!a || !b) return
    const h = Math.max(1, l.value * k)
    const x0 = a.x + nw
    const x1 = b.x
    const xm = (x0 + x1) / 2
    const y0 = a.out
    const y1 = b.in
    a.out += h
    b.in += h
    bands.push(
      <path
        // biome-ignore lint/suspicious/noArrayIndexKey: links keep their order
        key={i}
        className={l.tone ? `sk-link ${l.tone}` : 'sk-link'}
        d={`M${x0},${y0} C${xm},${y0} ${xm},${y1} ${x1},${y1} L${x1},${y1 + h} C${xm},${y1 + h} ${xm},${y0 + h} ${x0},${y0 + h} Z`}
      >
        <title>{`${a.n.label} → ${b.n.label}: ${format(l.value)}`}</title>
      </path>,
    )
  })
  return (
    <div ref={ref}>
      <svg
        className="chart sk"
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={label}
      >
        {bands}
        {[...pos.values()].map((p) => (
          <g key={p.n.id}>
            <rect
              className={p.n.tone ? `sk-node ${p.n.tone}` : 'sk-node'}
              x={p.x}
              y={p.y}
              width={nw}
              height={p.h}
              rx={1.5}
            />
            <text className="sk-t" x={p.x + nw + 6} y={p.y + Math.min(p.h / 2, 11) + 4}>
              {p.n.label} <tspan className="sk-v">{format(p.n.value)}</tspan>
            </text>
          </g>
        ))}
      </svg>
    </div>
  )
}
